'use client';

/**
 * ThemeProvider Adapter for next-themes
 * 
 * This component provides theme functionality using next-themes.
 * Enhanced with better SSR handling and error prevention.
 */
import React, { useEffect, useState } from 'react';
import { ThemeProvider as NextThemesProvider, useTheme as useNextTheme } from 'next-themes';

// Theme storage key - must match what's used in ThemeSwitcher
export const THEME_STORAGE_KEY = 'app-theme';

type Theme = 'light' | 'dark' | 'system';

/**
 * Custom hook that wraps useNextTheme and adds client-side safety
 * This version prevents SSR issues and readonly property errors
 */
export const useTheme = () => {
  const [mounted, setMounted] = useState(false);
  const nextTheme = useNextTheme();
  
  // After mounting, we can safely show the UI
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Safe version of setTheme that won't cause errors during SSR
  const safeSetTheme = (theme: string) => {
    if (!mounted) return;
    
    try {
      // Delay theme change to avoid readonly property error
      setTimeout(() => {
        nextTheme.setTheme(theme);
      }, 0);
    } catch (error) {
      console.error('Error setting theme:', error);
    }
  };
  
  // During SSR and first mount, return a default theme state
  // to avoid hydration mismatch
  if (!mounted) {
    return {
      theme: undefined,
      resolvedTheme: undefined,
      setTheme: safeSetTheme,
      systemTheme: undefined,
      themes: [],
    };
  }
  
  return {
    ...nextTheme,
    setTheme: safeSetTheme
  };
};

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

/**
 * ThemeProvider component
 *
 * This component provides theme context to the application using next-themes.
 * It includes error prevention and SSR-safe behavior.
 * 
 * @example
 * ```tsx
 * <ThemeProvider defaultTheme="system">
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({
  children,
  defaultTheme = 'system',
}: ThemeProviderProps) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme={defaultTheme}
      enableSystem
      enableColorScheme
      disableTransitionOnChange
      storageKey={THEME_STORAGE_KEY}
    >
      {children}
    </NextThemesProvider>
  );
}
